import { indexedDBService } from './IndexedDBService';
import { csvParserService } from './CsvParserService';

/**
 * Represents the progress of a download operation
 */
export interface DownloadProgress {
  /** Total number of items to download */
  total: number;
  /** Number of items that have been downloaded */
  completed: number;
  /** Percentage of download completion (0-100) */
  percentage: number;
  /** Current status of the download operation */
  status: 'idle' | 'downloading' | 'processing' | 'complete' | 'error';
  /** Error message if status is 'error' */
  error?: string;
}

/**
 * Options for downloading past year data
 */
interface DownloadOptions {
  /** Array of entity IDs to download data for */
  entityIds: number[];
  /** Type of record to download */
  recordType: string;
  /** Optional callback for progress updates */
  onProgress?: (progress: DownloadProgress) => void;
}

/**
 * Represents a month that needs to be downloaded
 */
interface MonthToDownload {
  /** Entity ID */
  entityId: number;
  /** Year */
  year: number;
  /** Month (1-12) */
  month: number;
}

/**
 * Represents date range boundaries
 */
interface DateRangeBoundaries {
  /** Minimum year in the range */
  minYear: number;
  /** Maximum year in the range */
  maxYear: number;
  /** Minimum month in the range */
  minMonth: number;
  /** Maximum month in the range */
  maxMonth: number;
}

/**
 * Service for interacting with S3 data storage
 * Handles downloading, processing, and caching of CSV data from S3
 */
class S3DataService {
  /** Base URL for the S3 CSV API */
  private readonly baseUrl = '/api/v4/reports/s3_csv';

  // =========================================================================
  // PUBLIC METHODS
  // =========================================================================

  /**
   * Downloads data for the past year for specified entities and record type
   * This is the main entry point for the service
   *
   * @param options Options for downloading past year data
   * @returns Aggregated data from IndexedDB
   */
  async downloadPastYearData(options: DownloadOptions): Promise<any> {
    const { entityIds, recordType, onProgress } = options;
    const monthsToDownload = await this.determineMonthsToDownload(entityIds, recordType);

    const totalToDownload = monthsToDownload.length;
    const progress: DownloadProgress = {
      total: totalToDownload,
      completed: 0,
      percentage: 0,
      status: 'downloading'
    };

    if (onProgress) {
      onProgress(progress);
    }

    try {
      let completedDownloads = 0;

      // If there are no months to download, skip the API call
      if (monthsToDownload.length === 0) {
        return await this.handleNoMonthsToDownload(entityIds, recordType, progress, onProgress);
      }

      // Find the min and max year-month combinations to set the date range
      const { minYear, maxYear, minMonth, maxMonth } =
        this.findDateRangeBoundaries(monthsToDownload);

      const urlDataHash = await this.fetchBatchedPresignedUrls(
        minYear,
        maxYear,
        minMonth,
        maxMonth,
        recordType
      );

      completedDownloads = await this.processUrlDataHash(
        urlDataHash,
        monthsToDownload,
        recordType,
        progress,
        onProgress
      );

      // Now that all data has been downloaded and saved to IndexedDB,
      // load the aggregated data from IndexedDB and update progress
      return await this.loadAggregatedDataAndUpdateProgress(
        entityIds,
        recordType,
        progress,
        onProgress
      );
    } catch (error) {
      // Handle errors
      if (onProgress) {
        onProgress({
          ...progress,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        });
      }
      throw error;
    }
  }

  /**
   * Fetches batched presigned URLs for S3 CSV files
   *
   * @param startYear The starting year for the date range
   * @param endYear The ending year for the date range
   * @param startMonth The starting month for the date range
   * @param endMonth The ending month for the date range
   * @param recordType The type of record to retrieve
   * @returns Object containing presigned URLs for each entity and date
   */
  async fetchBatchedPresignedUrls(
    startYear: number,
    endYear: number,
    startMonth: number,
    endMonth: number,
    recordType: string
  ): Promise<{ [key: string]: string }> {
    const response = await fetch(
      `${this.baseUrl}?start_year=${startYear}&end_year=${endYear}&start_month=${startMonth}&end_month=${endMonth}&record_type=${recordType}`
    );

    const data = await response.json();
    return data;
  }

  /**
   * Downloads and processes a CSV file from a presigned URL
   *
   * @param url The presigned URL to download the CSV from
   * @param recordType The type of record being downloaded
   * @returns Processed CSV data
   */
  async downloadAndProcessCsv(url: string, recordType: string): Promise<any> {
    try {
      // Try with the default mode first (which allows reading the response)
      const response = await fetch(url, {
        credentials: 'omit', // Don't send cookies to S3
        headers: {
          Accept: 'text/csv,application/json,text/plain,*/*'
        }
      });
      if (!response.ok) {
        return null;
      }

      const data = await response.text();
      return csvParserService.parseCSV(data, recordType);
    } catch (error) {
      console.error('Error downloading or processing CSV:', error);
      throw error;
    }
  }

  // =========================================================================
  // PRIVATE METHODS - Data Processing
  // =========================================================================

  /**
   * Processes the URL data hash, downloading and saving data for each month
   *
   * @param urlDataHash Hash of presigned URLs for each entity and month
   * @param monthsToDownload Array of months that need to be downloaded
   * @param recordType The type of record to download
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Number of completed downloads
   */
  private async processUrlDataHash(
    urlDataHash: { [key: string]: any },
    monthsToDownload: MonthToDownload[],
    recordType: string,
    progress: DownloadProgress,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<number> {
    let completedDownloads = 0;
    const totalToDownload = monthsToDownload.length;

    for (const [entityId, monthlyUrlKeyValuePair] of Object.entries(urlDataHash)) {
      for (const [yearMonthKey, url] of Object.entries(monthlyUrlKeyValuePair)) {
        const [year, month] = yearMonthKey.split('-');
        const entityIdNum = parseInt(entityId);
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);

        // Check if this month is in the monthsToDownload array
        const shouldDownload = monthsToDownload.some(
          (item) =>
            item.entityId === entityIdNum && item.year === yearNum && item.month === monthNum
        );

        // Skip if we already have this data
        if (!shouldDownload) {
          continue;
        }

        const data = await this.downloadAndProcessCsv(url as string, recordType);
        await indexedDBService.saveChartData(
          entityIdNum,
          yearNum,
          monthNum,
          recordType,
          data
            ? data
            : {
                reportNotFound: true
              },
          recordType
        );
        completedDownloads++;
        progress.completed = completedDownloads;
        progress.percentage =
          totalToDownload > 0 ? Math.round((completedDownloads / totalToDownload) * 100) : 100;

        if (onProgress) {
          onProgress({ ...progress });
        }
      }
    }

    return completedDownloads;
  }

  /**
   * Loads aggregated data from IndexedDB and updates progress
   *
   * @param entityIds Array of entity IDs to get data for
   * @param recordType The type of record to get data for
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Aggregated data from IndexedDB
   */
  private async loadAggregatedDataAndUpdateProgress(
    entityIds: number[],
    recordType: string,
    progress: DownloadProgress,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<any> {
    // Mark as processing before loading from IndexedDB
    if (onProgress) {
      onProgress({
        ...progress,
        status: 'processing'
      });
    }

    // Load the aggregated data from IndexedDB
    const allData = await indexedDBService.getAggregatedData(entityIds, recordType);

    // Mark as complete
    if (onProgress) {
      onProgress({
        ...progress,
        status: 'complete'
      });
    }

    return allData;
  }

  /**
   * Handles the case when there are no months to download
   *
   * @param entityIds Array of entity IDs to get data for
   * @param recordType The type of record to get data for
   * @param progress Current progress object
   * @param onProgress Optional callback for progress updates
   * @returns Aggregated data from IndexedDB
   */
  private async handleNoMonthsToDownload(
    entityIds: number[],
    recordType: string,
    progress: DownloadProgress,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<any> {
    // Mark as processing before loading from IndexedDB
    if (onProgress) {
      onProgress({
        ...progress,
        status: 'processing'
      });
    }

    // Load the aggregated data from IndexedDB
    const allData = await indexedDBService.getAggregatedData(entityIds, recordType);

    // Mark as complete
    if (onProgress) {
      onProgress({
        ...progress,
        status: 'complete'
      });
    }

    return allData;
  }

  // =========================================================================
  // PRIVATE METHODS - Data Analysis
  // =========================================================================

  /**
   * Determines which months need to be downloaded for each entity
   *
   * @param entityIds Array of entity IDs to check
   * @param recordType The type of record to check
   * @returns Array of objects containing entityId, year, and month for data that needs to be downloaded
   */
  private async determineMonthsToDownload(
    entityIds: number[],
    recordType: string
  ): Promise<MonthToDownload[]> {
    const monthsToDownload: MonthToDownload[] = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

    // For each entity, check which months need to be downloaded
    for (const entityId of entityIds) {
      for (let i = 0; i < 12; i++) {
        // Calculate year and month
        let month = currentMonth - i;
        let year = currentYear;

        if (month <= 0) {
          month += 12;
          year -= 1;
        }

        // Check if data exists in IndexedDB
        const existingData = await indexedDBService.getChartData(entityId, year, month, recordType);

        if (!existingData) {
          // If not in IndexedDB, add to the list of months to download
          monthsToDownload.push({ entityId, year, month });
        }
      }
    }

    return monthsToDownload;
  }

  /**
   * Finds the minimum and maximum year-month combinations from the months to download
   *
   * @param monthsToDownload Array of objects containing entityId, year, and month
   * @returns Object containing minYear, maxYear, minMonth, and maxMonth
   */
  private findDateRangeBoundaries(monthsToDownload: MonthToDownload[]): DateRangeBoundaries {
    let minYear = Number.MAX_SAFE_INTEGER;
    let maxYear = Number.MIN_SAFE_INTEGER;
    let minMonth = Number.MAX_SAFE_INTEGER;
    let maxMonth = Number.MIN_SAFE_INTEGER;

    // Initialize with the first month to download
    if (monthsToDownload.length > 0) {
      minYear = maxYear = monthsToDownload[0].year;
      minMonth = maxMonth = monthsToDownload[0].month;
    }

    // Find the min and max values
    for (const { year, month } of monthsToDownload) {
      // Update min year and month
      if (year < minYear || (year === minYear && month < minMonth)) {
        minYear = year;
        minMonth = month;
      }

      // Update max year and month
      if (year > maxYear || (year === maxYear && month > maxMonth)) {
        maxYear = year;
        maxMonth = month;
      }
    }

    return { minYear, maxYear, minMonth, maxMonth };
  }
}

/**
 * Singleton instance of the S3DataService
 */
export const s3DataService = new S3DataService();
